import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import styles from "./Assessment.module.scss";
import { fetchAssessmentsAPI, fetchAssessmentByIdAPI, fetchAssessmentStatusesAPI } from "@/services/api/assessment";
import AssessmentList from "./Components/AssessmentList";
import AssessmentDetail from "./Components/AssessmentDetail";
import AssessmentEditor from "./Components/AssessmentEditor";
import { useSearchParams } from "react-router-dom";

interface AssessmentData {
  id: number;
  assessmentId?: number;
  name: string;
  challengeCount: number;
  questionCount: number;
  freeformCount: number;
  invitedCount: number;
  assessedCount: number;
  qualifiedCount: number;
  createdByName: string;
  createdAt: string;
  lastActivity?: string;
  candidates?: any[];
}

const Assessment: React.FC = () => {
  const [assessments, setAssessments] = useState<AssessmentData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const perPage = 10;

  // URL Persistence
  const [searchParams, setSearchParams] = useSearchParams();
  const urlId = searchParams.get("id");
  const urlMode = searchParams.get("mode");

  const [selectedAssessment, setSelectedAssessment] = useState<AssessmentData | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [statuses, setStatuses] = useState<{ id: number; name: string; isActive: boolean }[]>([]);

  useEffect(() => {
    const getStatuses = async () => {
      try {
        const res = await fetchAssessmentStatusesAPI();
        const data = res?.data || res;
        if (Array.isArray(data)) {
          setStatuses(data.filter((s: any) => s.isActive));
        }
      } catch (err) {
        console.error("Failed to load statuses", err);
      }
    };
    getStatuses();
  }, []);

  // Derive activeStatus dynamically from searchParams (?tab=0,1,2)
  const tabValue = searchParams.get("tab");
  const activeStatus = useMemo(() => {
    if (statuses.length === 0) return null;
    const index = parseInt(tabValue || "0");
    if (isNaN(index) || index < 0 || index >= statuses.length) {
      return statuses[0];
    }
    return statuses[index];
  }, [tabValue, statuses]);

  const loadAssessments = useCallback(async (searchTerm: string = "", pageNo: number = 1, statusId?: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchAssessmentsAPI(searchTerm, pageNo, perPage, statusId);
      const innerData = res?.data || res;
      
      // Extensive check for pagination wrappers (results, content, or raw array)
      let list = [];
      if (innerData?.results && Array.isArray(innerData.results)) list = innerData.results;
      else if (innerData?.content && Array.isArray(innerData.content)) list = innerData.content;
      else if (Array.isArray(innerData)) list = innerData;
      else if (innerData?.data && Array.isArray(innerData.data)) list = innerData.data;

      setAssessments(list);
      setTotalRecords(innerData?.totalRecord || 0);
    } catch (err: any) {
      console.error("Fetch failed", err);
      setError("Failed to fetch assessments.");
    } finally {
      setLoading(false);
    }
  }, [perPage]);

  useEffect(() => {
    const sync = async () => {
      if (urlId && urlId !== "undefined" && urlId !== "null") {
        const idNum = parseInt(urlId);
        if (isNaN(idNum)) return;
        if (!selectedAssessment || (selectedAssessment.id !== idNum && selectedAssessment.assessmentId !== idNum)) {
          try {
            const res = await fetchAssessmentByIdAPI(idNum);
            const data = res?.data || res;
            if (data) setSelectedAssessment(data);
          } catch (e) {
            console.error(e);
          }
        }
        setIsEditing(urlMode === "edit");
      } else {
        setSelectedAssessment(null);
        setIsEditing(urlMode === "edit"); // Allowing create mode (mode=edit with no id)
      }
    };
    sync();
  }, [urlId, urlMode]);

  const prevSearch = useRef(search);
  const prevStatus = useRef(activeStatus?.id);

  useEffect(() => {
    if (statuses.length === 0) return;

    let targetPage = page;
    if (search !== prevSearch.current || activeStatus?.id !== prevStatus.current) {
      targetPage = 1;
      setPage(1);
      prevSearch.current = search;
      prevStatus.current = activeStatus?.id;
    }

    const timer = setTimeout(() => {
      loadAssessments(search, targetPage, activeStatus?.id);
    }, 300);

    return () => clearTimeout(timer);
  }, [search, page, activeStatus, statuses.length, loadAssessments]);

  const handleSelect = (item: AssessmentData | null) => {
    const currentTab = searchParams.get("tab") || "0";
    if (item) {
      setSearchParams({ tab: currentTab, id: (item.assessmentId || item.id).toString(), mode: "detail" });
    } else {
      setSearchParams({ tab: currentTab });
    }
  };

  const handleEditToggle = (val: boolean) => {
    const currentTab = searchParams.get("tab") || "0";
    if (selectedAssessment) {
      setSearchParams({ 
        tab: currentTab,
        id: (selectedAssessment.assessmentId || selectedAssessment.id).toString(), 
        mode: val ? "edit" : "detail" 
      });
    } else if (val) {
      setSearchParams({ tab: currentTab, mode: "edit" });
    }
  };

  // Aid resolution
  const aid = selectedAssessment ? (selectedAssessment.assessmentId || selectedAssessment.id) : 0;

  return (
    <div className={styles.container}>
      {isEditing ? (
        <AssessmentEditor
          assessmentId={aid}
          onBack={() => {
            loadAssessments(search, page, activeStatus?.id);
            handleSelect(null);
          }}
          onSave={(targetTab?: string) => {
            if (targetTab !== undefined) {
              setSearchParams({ tab: targetTab });
            } else {
              loadAssessments(search, page, activeStatus?.id);
              handleSelect(null);
            }
          }}
        />
      ) : selectedAssessment ? (
        <AssessmentDetail
          assessment={selectedAssessment}
          onBack={() => handleSelect(null)}
          onEdit={() => handleEditToggle(true)}
        />
      ) : (
        <AssessmentList
          assessments={assessments || []}
          loading={loading || statuses.length === 0}
          error={error}
          search={search}
          setSearch={setSearch}
          activeTab={activeStatus?.name?.toUpperCase() || ""}
          onSelect={handleSelect}
          onEdit={(item) => setSearchParams({ tab: searchParams.get("tab") || "0", id: (item.assessmentId || item.id).toString(), mode: "edit" })}
          onCreate={() => setSearchParams({ tab: searchParams.get("tab") || "0", mode: "edit" })}
          page={page}
          setPage={setPage}
          totalRecords={totalRecords}
          perPage={perPage}
          onRefresh={() => loadAssessments(search, page, activeStatus?.id)}
          tabs={statuses.map(s => s.name.toUpperCase())}
        />
      )}
    </div>
  );
};

export default Assessment;
